import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState, type FormEvent } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { ApiError, api } from '../api/client.js'
import type { Staff } from '../api/types.js'
import { Icon } from '../components/icons.js'
import { useToast } from '../components/toast.js'
import { EmptyState, Field, Modal, Notice, Spinner } from '../components/ui.js'
import { formatDate, formatDateTime, initials, todayIso } from '../lib/format.js'
import { DeleteStaffModal } from './DeleteStaffModal.js'
import { PageHead } from './Layout.js'
import { TeamsPanel } from './Teams.js'
import { useTeams } from '../components/people.js'
import { ListImportModal } from '../components/ListImport.js'

interface Invitation {
  link: string
  code: string
  mailed: boolean
  expiresInDays: number
}

type View = 'active' | 'inactive' | 'planning' | 'unregistered' | 'all' | 'teams'
type Sort = 'name' | 'lastSeen' | 'devices'

const VIEWS: { key: View; label: string }[] = [
  { key: 'active', label: 'Aktiv' },
  { key: 'unregistered', label: 'Noch nicht angemeldet' },
  { key: 'planning', label: 'Tourenplanung' },
  { key: 'inactive', label: 'Deaktiviert' },
  { key: 'all', label: 'Alle' },
  { key: 'teams', label: 'Gruppen' },
]

export function StaffPage() {
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState<Staff | 'new' | null>(null)
  const [invitation, setInvitation] = useState<{ staff: Staff; data: Invitation } | null>(null)
  const [deleting, setDeleting] = useState<string | null>(null)
  const [importing, setImporting] = useState(false)
  const [params] = useSearchParams()
  const [view, setView] = useState<View>(params.get('ansicht') === 'gruppen' ? 'teams' : 'active')
  const teams = useTeams()
  const [sort, setSort] = useState<Sort>('name')
  const [term, setTerm] = useState('')
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const togglePick = (id: string) =>
    setPicked((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  const toast = useToast()
  const [error, setError] = useState<string | null>(null)

  const { data, isLoading } = useQuery({ queryKey: ['staff'], queryFn: () => api<Staff[]>('/api/staff') })

  const invite = useMutation({
    mutationFn: (staff: Staff) => api<Invitation>(`/api/staff/${staff.id}/invite`, { method: 'POST' }),
    onSuccess: (result, staff) => setInvitation({ staff, data: result }),
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Einladung fehlgeschlagen.'),
  })

  const toggleActive = useMutation({
    mutationFn: (staff: Staff) => api(`/api/staff/${staff.id}/${staff.active ? 'deactivate' : 'activate'}`, { method: 'POST' }),
    onSuccess: async (_result, staff) => {
      toast(staff.active ? `${staff.name} deaktiviert. Angemeldete Geräte haben keinen Zugang mehr.` : `${staff.name} wieder aktiv.`, {
        action: { label: 'Rückgängig', onAction: () => toggleActive.mutate({ ...staff, active: !staff.active }) },
      })
      await queryClient.invalidateQueries({ queryKey: ['staff'] })
    },
  })

  const counts = useMemo(() => {
    const all = data ?? []
    return {
      active: all.filter((s) => s.active).length,
      inactive: all.filter((s) => !s.active).length,
      planning: all.filter((s) => s.active && s.planningAccess).length,
      unregistered: all.filter((s) => s.active && s.activeDevices === 0).length,
      all: all.length,
      teams: teams.data?.length ?? 0,
    }
  }, [data, teams.data])

  const rows = useMemo(() => {
    const q = term.trim().toLowerCase()
    const list = (data ?? []).filter((s) => {
      if (view === 'active' && !s.active) return false
      if (view === 'inactive' && s.active) return false
      if (view === 'planning' && !(s.active && s.planningAccess)) return false
      if (view === 'unregistered' && !(s.active && s.activeDevices === 0)) return false
      if (!q) return true
      return s.name.toLowerCase().includes(q) || (s.phone ?? '').replace(/\s/g, '').includes(q.replace(/\s/g, '')) || (s.email ?? '').toLowerCase().includes(q)
    })
    list.sort((a, b) => {
      if (sort === 'lastSeen') return (b.lastSeenAt ?? '').localeCompare(a.lastSeenAt ?? '') || a.name.localeCompare(b.name, 'de')
      if (sort === 'devices') return b.activeDevices - a.activeDevices || a.name.localeCompare(b.name, 'de')
      return a.name.localeCompare(b.name, 'de')
    })
    return list
  }, [data, view, term, sort])

  return (
    <>
      <PageHead
        title="Mitarbeiter"
        subtitle="Mitarbeiter melden sich per Einladungslink an. Kein Passwort, keine App aus dem Store."
        action={
          <div className="actions">
            <a className="btn secondary" href="/api/lists/staff.xlsx">
              <Icon name="download" /> Als Excel
            </a>
            <button type="button" className="btn secondary" onClick={() => setImporting(true)}>
              <Icon name="upload" /> Aus Excel importieren
            </button>
            <button type="button" className="btn" onClick={() => setEditing('new')}>
              <Icon name="plus" /> Neuer Mitarbeiter
            </button>
          </div>
        }
      />
      <ListImportModal
        open={importing}
        onClose={() => setImporting(false)}
        title="Mitarbeiter aus Excel"
        endpoint="/api/staff/import"
        nouns={['Mitarbeiter', 'Mitarbeiter']}
        onDone={() => void queryClient.invalidateQueries({ queryKey: ['staff'] })}
        help={
          <p style={{ marginTop: 0 }}>
            Spalten: <strong>Name</strong> (Pflicht), E-Mail, Telefon, Sprache (de, tr, pl, uk, ar), Planung im Browser (ja oder nein). Wer schon da
            ist (gleiche E-Mail oder gleicher Name), wird ergänzt, alle anderen neu angelegt. Einladungen gehen erst raus, wenn Sie sie verschicken.
            Die Datei aus „Als Excel“ passt direkt.
          </p>
        }
      />

      {error && <Notice kind="error">{error}</Notice>}

      <div className="toolbar">
        <div className="toolbar-row">
          <div className="segmented" role="group" aria-label="Ansicht">
            {VIEWS.map((v) => (
              <button key={v.key} type="button" aria-pressed={view === v.key} onClick={() => setView(v.key)}>
                {v.label} <span className="muted">{counts[v.key]}</span>
              </button>
            ))}
          </div>
          <div className="grow" />
          {view !== 'teams' && (<>
          <label className="field" style={{ maxWidth: 320 }}>
            <span>Suchen</span>
            <input type="search" placeholder="Name, Telefon, E-Mail" value={term} onChange={(e) => setTerm(e.target.value)} />
          </label>
          <label className="field" style={{ maxWidth: 200 }}>
            <span>Sortierung</span>
            <select value={sort} onChange={(e) => setSort(e.target.value as Sort)}>
              <option value="name">Name</option>
              <option value="lastSeen">Zuletzt aktiv</option>
              <option value="devices">Geräte</option>
            </select>
          </label>
          </>)}
        </div>
      </div>

      {view === 'teams' ? <TeamsPanel /> : (
      <section className="card">
        {isLoading ? (
          <Spinner />
        ) : !data || data.length === 0 ? (
          <EmptyState
            title="Noch keine Mitarbeiter"
            hint="Legen Sie einen Mitarbeiter an und schicken Sie ihm den Einladungslink."
            action={
              <button type="button" className="btn" onClick={() => setEditing('new')}>
                Neuer Mitarbeiter
              </button>
            }
          />
        ) : rows.length === 0 ? (
          <EmptyState title="Niemand passt zu Ansicht und Suche" />
        ) : (
          <div className="table-scroll">
            {picked.size > 0 && <StaffBulkBar ids={[...picked]} onClear={() => setPicked(new Set())} />}
            <table className="data">
              <thead>
                <tr>
                  <th className="check-col">
                    <input
                      type="checkbox"
                      aria-label="Alle sichtbaren auswählen"
                      checked={rows.length > 0 && rows.every((r) => picked.has(r.id))}
                      onChange={() => setPicked(rows.every((r) => picked.has(r.id)) ? new Set() : new Set(rows.map((r) => r.id)))}
                    />
                  </th>
                  <th>Name</th>
                  <th>Kontakt</th>
                  <th className="nowrap">Geräte</th>
                  <th className="nowrap">Zuletzt aktiv</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((staff) => (
                  <tr key={staff.id} style={staff.active ? undefined : { opacity: 0.55 }} className={picked.has(staff.id) ? 'selected' : undefined}>
                    <td className="check-col">
                      <input type="checkbox" aria-label={`${staff.name} auswählen`} checked={picked.has(staff.id)} onChange={() => togglePick(staff.id)} />
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <span className="avatar sm">{initials(staff.name)}</span>
                        <Link to={`/mitarbeiter/${staff.id}`} className="strong">
                          {staff.name}
                        </Link>
                        {staff.planningAccess && <span className="badge open">Tourenplanung</span>}
                        {!staff.active && <span className="badge skipped">inaktiv</span>}
                        {staff.active && staff.absence && <AbsenceBadge absence={staff.absence} />}
                        {staff.active && staff.coveringTasks ? (
                          <CoveringBadge count={staff.coveringTasks} staffId={staff.id} coveringFor={staff.coveringFor} />
                        ) : null}
                      </div>
                    </td>
                    <td className="small">
                      {staff.email ?? <span className="muted">keine E-Mail</span>}
                      {staff.phone && <div className="muted">{staff.phone}</div>}
                    </td>
                    <td className="nowrap">
                      {staff.activeDevices > 0 ? (
                        <span className="badge done">{staff.activeDevices} angemeldet</span>
                      ) : (
                        <span className="badge">nicht angemeldet</span>
                      )}
                    </td>
                    <td className="nowrap small">{staff.lastSeenAt ? formatDateTime(staff.lastSeenAt) : <span className="muted">nie</span>}</td>
                    <td style={{ textAlign: 'right' }} className="nowrap">
                      {staff.active && (
                        <button type="button" className="btn secondary small" disabled={invite.isPending} onClick={() => invite.mutate(staff)}>
                          Einladen
                        </button>
                      )}{' '}
                      {staff.active && (
                        <Link className="btn ghost small" to={`/mitarbeiter/${staff.id}/vertretung`} title="Krankmeldung oder Urlaub: Aufgaben verteilen">
                          <Icon name="swap" size={15} /> Vertretung
                        </Link>
                      )}{' '}
                      <button type="button" className="btn ghost small" onClick={() => setEditing(staff)}>
                        <Icon name="edit" size={15} /> Bearbeiten
                      </button>{' '}
                      <button type="button" className="btn ghost small" onClick={() => toggleActive.mutate(staff)}>
                        <Icon name="archive" size={15} /> {staff.active ? 'Deaktivieren' : 'Aktivieren'}
                      </button>{' '}
                      <button type="button" className="btn ghost small" onClick={() => setDeleting(staff.id)}>
                        Löschen
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      )}

      <StaffFormModal
        open={editing !== null}
        staff={editing === 'new' ? null : editing}
        onClose={() => setEditing(null)}
        onSaved={() => setEditing(null)}
      />
      <InvitationModal invitation={invitation} onClose={() => setInvitation(null)} />
      <DeleteStaffModal staffId={deleting} onClose={() => setDeleting(null)} onDeleted={() => setDeleting(null)} />
    </>
  )
}

/** Create and edit share one form; everything set at creation stays editable. */
export function StaffFormModal({
  open,
  staff,
  onClose,
  onSaved,
}: {
  open: boolean
  staff: Staff | { id: string; name: string; email: string | null; phone: string | null; planningAccess: boolean } | null
  onClose: () => void
  onSaved: () => void
}) {
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      staff ? api(`/api/staff/${staff.id}`, { method: 'PATCH', body }) : api<{ id: string }>('/api/staff', { method: 'POST', body }),
    onSuccess: async () => {
      setError(null)
      await queryClient.invalidateQueries({ queryKey: ['staff'] })
      await queryClient.invalidateQueries({ queryKey: ['staff-detail'] })
      onSaved()
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Speichern fehlgeschlagen.'),
  })

  return (
    <Modal
      open={open}
      title={staff ? 'Mitarbeiter bearbeiten' : 'Neuer Mitarbeiter'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn secondary" onClick={onClose}>
            Abbrechen
          </button>
          <button type="submit" form="staff-form" className="btn" disabled={save.isPending}>
            Speichern
          </button>
        </>
      }
    >
      {error && <Notice kind="error">{error}</Notice>}
      <form
        id="staff-form"
        key={staff?.id ?? 'new'}
        onSubmit={(event: FormEvent<HTMLFormElement>) => {
          event.preventDefault()
          const form = new FormData(event.currentTarget)
          save.mutate({
            name: form.get('name'),
            email: (form.get('email') as string) || (staff ? '' : undefined),
            phone: (form.get('phone') as string) || (staff ? '' : undefined),
            planningAccess: form.get('planningAccess') === 'on',
          })
        }}
      >
        <Field label="Name">
          <input name="name" required autoFocus defaultValue={staff?.name} />
        </Field>
        <Field label="E-Mail-Adresse" hint="Wenn angegeben, geht die Einladung automatisch per E-Mail raus.">
          <input name="email" type="email" defaultValue={staff?.email ?? ''} />
        </Field>
        <Field label="Telefon" hint="Für den Fall, dass Sie den Code durchgeben müssen.">
          <input name="phone" defaultValue={staff?.phone ?? ''} />
        </Field>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 4 }}>
          <input type="checkbox" name="planningAccess" defaultChecked={staff?.planningAccess ?? false} style={{ width: 18, height: 18, marginTop: 2 }} />
          <span>
            <span className="strong">Tourenplanung im Browser</span>
            <div className="muted small">Tagesliste sortieren, Route öffnen, nach Excel. Für Touren mit vielen Adressen am Tag.</div>
          </span>
        </label>
      </form>
    </Modal>
  )
}

/**
 * The invitation is shown in full, link and code, not just mailed. On the
 * first pilot day a Verwalter will often stand next to the worker and simply
 * read it out, and that must be possible without an e-mail account.
 */
function InvitationModal({ invitation, onClose }: { invitation: { staff: Staff; data: Invitation } | null; onClose: () => void }) {
  const [copied, setCopied] = useState(false)

  return (
    <Modal
      open={invitation !== null}
      title={`Einladung für ${invitation?.staff.name ?? ''}`}
      onClose={onClose}
      footer={
        <button type="button" className="btn" onClick={onClose}>
          Fertig
        </button>
      }
    >
      {invitation && (
        <>
          {invitation.data.mailed ? (
            <Notice kind="success">Die Einladung wurde an {invitation.staff.email} geschickt.</Notice>
          ) : (
            <Notice kind="warn">
              Es wurde keine E-Mail versendet{invitation.staff.email ? '' : ' (keine E-Mail-Adresse hinterlegt)'}. Geben Sie Link und Code direkt weiter.
            </Notice>
          )}

          <Field label="1. Diesen Link auf dem Handy öffnen">
            <input readOnly value={invitation.data.link} onFocus={(e) => e.currentTarget.select()} />
          </Field>
          <button
            type="button"
            className="btn secondary small"
            style={{ marginBottom: 18 }}
            onClick={async () => {
              await navigator.clipboard.writeText(invitation.data.link).catch(() => {})
              setCopied(true)
              setTimeout(() => setCopied(false), 2000)
            }}
          >
            {copied ? 'Kopiert' : 'Link kopieren'}
          </button>

          <div className="field">
            <span>2. Diesen Code eingeben</span>
            <div
              style={{
                fontSize: 38,
                fontWeight: 700,
                letterSpacing: 10,
                textAlign: 'center',
                padding: '14px 0',
                background: 'var(--green-50)',
                borderRadius: 12,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {invitation.data.code}
            </div>
            <div className="hint">Gültig für {invitation.data.expiresInDays} Tage. Danach bleibt das Handy dauerhaft angemeldet.</div>
          </div>
        </>
      )}
    </Modal>
  )
}

/** "krank bis 24.10." today, or "Urlaub ab 03.11." when it is still ahead. */
export function AbsenceBadge({ absence }: { absence: { kind: 'krank' | 'urlaub'; from: string; until: string } }) {
  const today = todayIso()
  const label = absence.kind === 'krank' ? 'krank' : 'Urlaub'
  const text = absence.from > today ? `${label} ab ${formatDate(absence.from)}` : `${label} bis ${formatDate(absence.until)}`
  return <span className={`badge ${absence.from > today ? '' : 'warn'}`}>{text}</span>
}

/**
 * "Macht gerade Vertretung", a hint for the Minijobgrenze. Names for whom on
 * hover; a click leads to the person's page, where the details are.
 */
export function CoveringBadge({ count, staffId, coveringFor }: { count: number; staffId?: string; coveringFor?: { name: string; tasks: number }[] }) {
  const forWhom = coveringFor?.length ? `für ${coveringFor.map((c) => `${c.name} (${c.tasks})`).join(', ')}. ` : ''
  const title = `${forWhom}Aufgaben in den nächsten 14 Tagen aus Plänen anderer Personen. Auf die Minijobgrenze achten.`
  const label = `macht Vertretung (${count})`
  if (!staffId) return <span className="badge info" title={title}>{label}</span>
  return (
    <Link className="badge info badge-link" title={title} to={`/mitarbeiter/${staffId}#vertretung`} onClick={(e) => e.stopPropagation()}>
      {label}
    </Link>
  )
}

/** Several people krank or im Urlaub in one step. */
function StaffBulkBar({ ids, onClear }: { ids: string[]; onClear: () => void }) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const today = todayIso()
  const [kind, setKind] = useState<'krank' | 'urlaub'>('krank')
  const [from, setFrom] = useState(today)
  const [until, setUntil] = useState(today)
  const run = useMutation({
    mutationFn: () => api<{ created: number }>('/api/absences/bulk', { method: 'POST', body: { ids, kind, from, until } }),
    onSuccess: async (result) => {
      toast(`${result.created} ${result.created === 1 ? 'Person' : 'Personen'} als ${kind === 'krank' ? 'krank' : 'im Urlaub'} eingetragen. Vertretung je Person über „Vertretung“ regeln.`)
      onClear()
      await queryClient.invalidateQueries({ queryKey: ['staff'] })
      await queryClient.invalidateQueries({ queryKey: ['absences'] })
      await queryClient.invalidateQueries({ queryKey: ['occurrences'] })
    },
    onError: (error) => toast(error instanceof ApiError ? error.message : 'Eintragen fehlgeschlagen.', 'error'),
  })
  return (
    <div className="bulk-bar" role="region" aria-label="Mehrfachauswahl Mitarbeiter">
      <span className="strong">{ids.length} ausgewählt</span>
      <div className="bulk-group">
        <select value={kind} onChange={(e) => setKind(e.target.value as 'krank' | 'urlaub')} aria-label="Art">
          <option value="krank">krank</option>
          <option value="urlaub">Urlaub</option>
        </select>
        <input type="date" value={from} onChange={(e) => e.target.value && setFrom(e.target.value)} aria-label="Von" />
        <span className="muted small">bis</span>
        <input type="date" value={until} min={from} onChange={(e) => e.target.value && setUntil(e.target.value)} aria-label="Bis" />
        <button type="button" className="btn small" disabled={run.isPending || until < from} onClick={() => run.mutate()}>
          Eintragen
        </button>
      </div>
      <button type="button" className="btn ghost small" onClick={onClear}>
        Auswahl aufheben
      </button>
    </div>
  )
}
